/**
 * PURE controller for the signed playback URL (F3-F).
 *
 * `GET /api/media/videos/:id/play?format=json` returns `{url, expiresAt}` with
 * a TTL (6h). The URL EXPIRES → the `<video>` emits `error`; the player requests a
 * new URL and restores the position. This controller caches the URL, knows if it's
 * expired (with margin), and de-duplicates concurrent requests. The network is injected
 * (`fetcher`) → testable with fake timers and a mock, no DOM or real `fetch`.
 */

export interface PlayUrl {
  url: string;
  /** ISO-8601 expiry (or epoch ms, tolerated). */
  expiresAt: string;
}

export type PlayUrlFetcher = (videoId: string) => Promise<PlayUrl>;

export interface PlayUrlControllerOptions {
  /** Seconds before `expiresAt` at which it's already considered "about to expire". */
  skewSeconds?: number;
  /** Injectable clock (ms). Defaults to `Date.now`. */
  now?: () => number;
}

export interface PlayUrlController {
  /** Current URL (refreshes if missing or expired). De-dupes concurrent calls. */
  get(): Promise<string>;
  /** Forces a refetch (after a `<video>` `error` from an expired URL). */
  refresh(): Promise<string>;
  /** Cached URL without touching the network (or null). */
  peek(): string | null;
  /** Is the cached URL absent or expired (with margin)? */
  isStale(): boolean;
}

const DEFAULT_SKEW_SECONDS = 30;

function parseExpiry(expiresAt: string): number {
  const asNumber = Number(expiresAt);
  if (Number.isFinite(asNumber) && asNumber > 0) {
    return asNumber;
  }
  const parsed = Date.parse(expiresAt);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function createPlayUrlController(
  videoId: string,
  fetcher: PlayUrlFetcher,
  options: PlayUrlControllerOptions = {},
): PlayUrlController {
  const skewMs = (options.skewSeconds ?? DEFAULT_SKEW_SECONDS) * 1000;
  const now = options.now ?? Date.now;

  let cached: PlayUrl | null = null;
  let expiryMs = 0;
  let inflight: Promise<string> | null = null;

  function isStale(): boolean {
    if (!cached) {
      return true;
    }
    return now() >= expiryMs - skewMs;
  }

  async function fetchFresh(): Promise<string> {
    const result = await fetcher(videoId);
    cached = result;
    expiryMs = parseExpiry(result.expiresAt);
    return result.url;
  }

  function get(): Promise<string> {
    if (cached && !isStale()) {
      return Promise.resolve(cached.url);
    }
    if (inflight) {
      return inflight;
    }
    inflight = fetchFresh().finally(() => {
      inflight = null;
    });
    return inflight;
  }

  function refresh(): Promise<string> {
    cached = null;
    expiryMs = 0;
    return get();
  }

  return {
    get,
    refresh,
    peek: () => cached?.url ?? null,
    isStale,
  };
}
